import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { fetchShotHistory } from '../services/api';
import './AIDefectDetectionModal.css';

/* 실측값은 소수점이 길게 붙어 오므로 2자리에서 끊는다. */
const round2 = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  const n = Number(v);
  return Number.isFinite(n) ? String(Math.round(n * 100) / 100) : v;
};

/**
 * AI 불량탐지 모달.
 * 양/불 탭의 사출정보 / Shot History / 공정검사(레이더) 세 영역을 한 화면에 모았다.
 * Shot History 에서 샷을 고르면 레이더 차트의 실측값이 그 샷 기준으로 다시 그려진다.
 */
function AIDefectDetectionModal({ open, onClose }) {
  const [shotHistoryData, setShotHistoryData] = useState([]);
  const [selectedShotNumber, setSelectedShotNumber] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await fetchShotHistory(null, 100);
        const shots = result?.data?.shots || [];
        const transformed = shots.map((shot) => ({
          shotNum: shot.shotCount,
          craftId: shot.craftId,
          metering: shot.chargePosi1,
          injectionTime: shot.injTime,
          injection: shot.injStartPosi,
          cushion: shot.injEndPosi,
          temperature: shot.temp1Current,
          speed: shot.injSpeed1,
          qualityStatus: shot.qualityStatus,
        }));
        if (cancelled) return;
        setShotHistoryData(transformed);
        setSelectedShotNumber(transformed.length ? transformed[0].shotNum : null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // ESC 로 닫기
  const onKey = useCallback((e) => { if (e.key === 'Escape') onClose(); }, [onClose]);
  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onKey]);

  if (!open) return null;

  const selected = shotHistoryData.find((s) => s.shotNum === selectedShotNumber) || null;
  const craftId = selected?.craftId || '-';
  const isGood = selected ? selected.qualityStatus === 'GOOD' : null;

  /* 헤더(.header)가 z-index 로 자체 쌓임 맥락을 만든다. 그 안에서 렌더하면
     본문의 absolute 요소(LSTM 불량 원인 분석 버튼 등)가 모달 위로 올라온다.
     document.body 로 포털해 쌓임 맥락을 벗어난다. */
  return createPortal((
    <div className="aidd-overlay" onClick={onClose}>
      <div className="aidd-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="aidd-head">
          <h2>
            <span className="material-symbols-outlined">search_check</span>
            AI 불량탐지
          </h2>
          <div className="aidd-head-right">
            <span className="aidd-sample-tag">예시 데이터</span>
            <button type="button" className="aidd-close" onClick={onClose} aria-label="닫기">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>

        <div className="aidd-body">
          {/* 사출정보 */}
          <section className="aidd-pane">
            <h3 className="aidd-title">사출정보</h3>
            <div className="aidd-product-image">
              <img src="/product-blueprint.png" alt="제품 이미지"
                   onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            </div>
            <dl className="aidd-info">
              <dt>제품번호</dt><dd>{craftId}</dd>
              <dt>제품명</dt><dd>{craftId !== '-' ? craftId + ' 사출품' : '-'}</dd>
              <dt>선택 샷</dt><dd>{selectedShotNumber ? '#' + selectedShotNumber : '-'}</dd>
              <dt>판정</dt>
              <dd>
                {isGood === null ? '-' : (
                  <span className={'aidd-badge ' + (isGood ? 'good' : 'bad')}>
                    {isGood ? '양품' : '불량'}
                  </span>
                )}
              </dd>
            </dl>
          </section>

          {/* Shot History */}
          <section className="aidd-pane">
            <h3 className="aidd-title">Shot History</h3>
            {loading ? (
              <div className="aidd-empty">불러오는 중...</div>
            ) : shotHistoryData.length === 0 ? (
              <div className="aidd-empty">샷 이력이 없습니다.</div>
            ) : (
              <div className="aidd-shot-list">
                <table className="aidd-shot-table">
                  <thead>
                    <tr>
                      <th>샷</th><th>계량위치</th><th>사출시간</th>
                      <th>히터온도</th><th>사출속도</th><th>쿠션량</th><th>양/불</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shotHistoryData.map((s) => (
                      <tr
                        key={s.shotNum}
                        className={s.shotNum === selectedShotNumber ? 'selected' : undefined}
                        onClick={() => setSelectedShotNumber(s.shotNum)}
                      >
                        <td>#{s.shotNum}</td>
                        <td>{round2(s.metering)}</td>
                        <td>{round2(s.injectionTime)}</td>
                        <td>{round2(s.temperature)}</td>
                        <td>{round2(s.speed)}</td>
                        <td>{round2(s.cushion)}</td>
                        <td>
                          <span className={'aidd-badge ' + (s.qualityStatus === 'GOOD' ? 'good' : 'bad')}>
                            {s.qualityStatus === 'GOOD' ? '양' : '불'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* 공정검사 */}
          <section className="aidd-pane aidd-pane-wide">
            <h3 className="aidd-title">공정검사</h3>
            <div className="aidd-radar">
            <ResponsiveContainer width="100%" height="100%" key={selectedShotNumber}>
            <RadarChart data={(() => {
              const selectedShot = selectedShotNumber
                ? shotHistoryData.find(s => s.shotNum === selectedShotNumber)
                : null;

              // 설정값 - 최근 10개 샷의 평균으로 계산
              const recentShots = shotHistoryData.slice(0, 10).filter(s => s);
              const avg = (field) => {
                const vals = recentShots.map(s => s[field]).filter(v => v != null && !isNaN(v));
                return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
              };
              const settings = {
                metering: avg('metering') || 5.0,
                injection: avg('injection') || 12,
                temperature: avg('temperature') || 220,
                speed: avg('speed') || 30,
                cushion: avg('cushion') || 7.7,
                time: avg('injectionTime') || 5.3
              };

              // 실측값 (선택된 샷이 있으면 해당 샷 데이터, 없으면 기본값)
              const measured = selectedShot ? {
                metering: selectedShot.metering,
                injection: selectedShot.injection,
                temperature: selectedShot.temperature,
                speed: selectedShot.speed,
                cushion: selectedShot.cushion,
                time: selectedShot.injectionTime
              } : {
                metering: settings.metering,
                injection: settings.injection,
                temperature: settings.temperature,
                speed: settings.speed,
                cushion: settings.cushion,
                time: settings.time
              };

              // 정규화 함수 (설정값을 중심으로 70이 되도록 변환)
              const normalizeAroundSetting = (value, settingValue, range) => {
                // settingValue를 중심으로 ±range만큼을 0-100으로 매핑
                // settingValue가 정확히 70이 되도록 함
                const deviation = value - settingValue;  // 설정값과의 차이
                const normalizedDeviation = (deviation / range) * 30;  // ±30 범위로 정규화
                return 70 + normalizedDeviation;  // 70을 중심으로 ±30
              };

              return [
                {
                  subject: '계량위치',
                  설정값: 70,  // 항상 70
                  실측값: normalizeAroundSetting(measured.metering, settings.metering, 5),
                  fullMark: 100
                },
                {
                  subject: '사출위치',
                  설정값: 70,  // 항상 70
                  실측값: normalizeAroundSetting(measured.injection, settings.injection, 10),
                  fullMark: 100
                },
                {
                  subject: '히터온도',
                  설정값: 70,  // 항상 70
                  실측값: normalizeAroundSetting(measured.temperature, settings.temperature, 20),
                  fullMark: 100
                },
                {
                  subject: '사출속도',
                  설정값: 70,  // 항상 70
                  실측값: normalizeAroundSetting(measured.speed, settings.speed, 30),
                  fullMark: 100
                },
                {
                  subject: '쿠션량',
                  설정값: 70,  // 항상 70
                  실측값: normalizeAroundSetting(measured.cushion, settings.cushion, 5),
                  fullMark: 100
                },
                {
                  subject: '사출시간',
                  설정값: 70,  // 항상 70
                  실측값: normalizeAroundSetting(measured.time, settings.time, 5),
                  fullMark: 100
                }
              ];
            })()}>
              <PolarGrid stroke="#30363D" />
              <PolarAngleAxis
                dataKey="subject"
                tick={{ fill: '#9CA3AF', fontSize: 13 }}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 100]}
                ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                tick={{ fill: '#6B7280', fontSize: 11 }}
                stroke="#30363D"
              />
              <Radar
                name={'설정값'}
                dataKey="설정값"
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.2}
                strokeWidth={1}
              />
              <Radar
                name={'실측값'}
                dataKey="실측값"
                stroke="#10B981"
                fill="#10B981"
                fillOpacity={0.2}
                strokeWidth={1}
              />
              <Legend
                align="right"
                verticalAlign="top"
                wrapperStyle={{
                  fontSize: '12px',
                  paddingTop: '10px',
                  paddingRight: '20px'
                }}
                iconSize={8}
                iconType="circle"
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #30363D',
                  borderRadius: '8px',
                  color: '#E5E7EB',
                  fontSize: '12px'
                }}
                labelStyle={{ fontSize: '13px' }}
              />
            </RadarChart>
          </ResponsiveContainer>
            </div>
            {/* 공정검사 카드와 같은 구성: 실측값 표 + 우측 양/불 표 */}
            <div className="aidd-measured-row">
              <table className="aidd-measured aidd-measured-values">
                <thead>
                  <tr>
                    <th>계량위치</th><th>사출위치</th><th>히터온도</th>
                    <th>사출속도</th><th>쿠션량</th><th>사출시간</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{round2(selected?.metering)}mm</td>
                    <td>{round2(selected?.injection)}mm</td>
                    <td>{round2(selected?.temperature)}°C</td>
                    <td>{round2(selected?.speed)}mm/s</td>
                    <td>{round2(selected?.cushion)}mm</td>
                    <td>{round2(selected?.injectionTime)}s</td>
                  </tr>
                </tbody>
              </table>
              <table className="aidd-measured aidd-measured-judge">
                <thead>
                  <tr><th>양/불</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {isGood === null ? '-' : (
                        <span className={'aidd-badge ' + (isGood ? 'good' : 'bad')}>
                          {isGood ? '양품' : '불량'}
                        </span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  ), document.body);
}

export default AIDefectDetectionModal;
